import { useState } from 'react';

import { usePrevious } from './usePrevious';

type SetValue<Value> = (value: Value) => void;

type Params<Value> = {
  valueFromProps: Value | undefined;
  onChangeFromProps: SetValue<Value> | undefined;
  defaultValue: NonNullable<Value>;
};

export const useMaybeControlledValue = <Value>({
  valueFromProps,
  onChangeFromProps,
  defaultValue,
}: Params<Value>): [value: Value, setValue: SetValue<Value>, isControlled: boolean] => {
  const [valueFromState, setValueFromState] = useState<Value>(defaultValue);
  const isControlled = valueFromProps !== undefined && onChangeFromProps !== undefined;
  const previousIsControlled = usePrevious(isControlled);
  if (typeof previousIsControlled === 'boolean' && isControlled !== previousIsControlled) {
    throw new Error('A maybe controlled component must be either controlled OR uncontrolled for its whole lifetime');
  }
  if (!isControlled && (valueFromProps !== undefined || onChangeFromProps !== undefined)) {
    throw new Error('To control the value, provide both value and change handler');
  }
  const value = isControlled ? (valueFromProps as NonNullable<Value>) : valueFromState;
  const setValue = isControlled ? (onChangeFromProps as SetValue<Value>) : setValueFromState;
  return [value, setValue, isControlled];
};
